import React, { createElement, useCallback, useEffect, useState } from 'react';
import MessageDialog from '../molecules/MessageDialog';
import { ActionModalHeightContext } from '../atoms/Modal/ActionModalHeightContext';
import { useLatest } from './useLatest';
import { isAccidentalClose, type ModalCloseReason } from '../types/ModalCloseReason';

/** Тексты вопроса перед потерей несохранённого ввода. */
export interface IDiscardQuestion {
    /** Заголовок окна */
    header: string;
    /** Пояснение под заголовком */
    message: string;
    /** Кнопка потери ввода */
    confirmText: string;
    /** Кнопка возврата к вводу (по умолчанию в фокусе; Escape и щелчок по фону — она же) */
    cancelText: string;
}

/** Вопрос по умолчанию — у окон с вводом и у правок страниц. */
export const DISCARD_QUESTION: IDiscardQuestion = {
    header: 'Закрыть без сохранения?',
    message: 'Введённые данные не сохранятся.',
    confirmText: 'Не сохранять',
    cancelText: 'Вернуться',
};

export interface IModalCloseGuardOptions {
    /** Закрытие окна: отказ от действия. */
    onClose: () => void;
    /** Окно открыто; закрытие извне завершает текущий вопрос. */
    active?: boolean;
    /** Идёт выполнение (отправка, подписание, загрузка): закрыть окно нельзя. */
    closeDisabled?: boolean;
    /** Спрашивать ли перед закрытием; вызывается в момент закрытия, а не на каждом рендере. */
    hasUnsavedInput?: () => boolean;
    /** Ответ «Вернуться» (и Escape, и клик по фону у вопроса): окно остаётся открытым. */
    onKeep?: () => void;
    /** Свои тексты вопроса; чего нет — из `DISCARD_QUESTION`. */
    question?: Partial<IDiscardQuestion>;
}

export interface IModalCloseGuard {
    /** Escape и клик по фону спрашивают при несохранённом вводе; крестик, кнопка и вызов без причины закрывают сразу. */
    requestClose: (reason?: ModalCloseReason | React.MouseEvent<HTMLElement>) => void;
    /** Явная проверка перед потерей правок (уход со страницы, смена редактируемой строки). */
    confirmClose: () => void;
    /** Вопрос «Закрыть без сохранения?» — рендерить рядом с окном, а не внутри его формы. */
    confirmDialog: React.ReactElement | null;
}

/**
 * Закрытие модалки с защитой: во время выполнения закрыть нельзя, при Escape и клике по фону
 * с несохранённым вводом — сначала вопрос. Вопрос живёт отдельным окном поверх: внутри формы окна его клавиши и клики
 * всплывали бы по React-дереву в форму и в обработчики самого окна.
 */
export const useModalCloseGuard = ({
    onClose,
    active = true,
    closeDisabled = false,
    hasUnsavedInput,
    onKeep,
    question,
}: IModalCloseGuardOptions): IModalCloseGuard => {
    const [confirming, setConfirming] = useState(false);
    useEffect(() => {
        if (!active || closeDisabled) setConfirming(false);
    }, [active, closeDisabled]);
    const latest = useLatest({ onClose, active, closeDisabled, hasUnsavedInput, onKeep });

    const confirmClose = useCallback(() => {
        const current = latest.current;
        if (!current.active || current.closeDisabled) return;
        if (current.hasUnsavedInput?.()) {
            setConfirming(true);
            return;
        }
        current.onClose();
    }, [latest]);

    const requestClose = useCallback(
        (reason?: ModalCloseReason | React.MouseEvent<HTMLElement>) => {
            if (!latest.current.active) return;
            if (isAccidentalClose(reason)) {
                confirmClose();
            } else if (!latest.current.closeDisabled) {
                latest.current.onClose();
            }
        },
        [confirmClose, latest],
    );

    const texts = { ...DISCARD_QUESTION, ...question };
    // Вопрос — рядом с окном, а не внутри него: высоту окна действия (`fullHeight`) он наследовать не должен.
    const confirmDialog =
        active && confirming && !closeDisabled
            ? createElement(
                  ActionModalHeightContext,
                  { value: false },
                  createElement(MessageDialog, {
                      header: texts.header,
                      message: texts.message,
                      size: 's',
                      showOkButton: true,
                      showCancelButton: true,
                      okButtonText: texts.confirmText,
                      okButtonVariant: 'light',
                      okButtonTone: 'danger',
                      cancelButtonText: texts.cancelText,
                      onConfirm: () => {
                          setConfirming(false);
                          latest.current.onClose();
                          return Promise.resolve();
                      },
                      onCancel: () => {
                          setConfirming(false);
                          latest.current.onKeep?.();
                      },
                  }),
              )
            : null;

    return { requestClose, confirmClose, confirmDialog };
};
